import clsx from "clsx";
import { Layers, Plus, Package, Puzzle } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { basisLabel } from "../../shared/money";
import { plural } from "../../shared/words";
import { useConfirm } from "../../web/components/Confirm";
import { Badge, ErrorNotice, ListWarning, Notice, PageHeader, Panel } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { can, itemTax, shownCost, usePriceListSaver, whyNot, tagNamesOf } from "./data";
import { describePricingShort } from "./describe";
import { type CheckedMark, inUse, isProduct, itemSearchText, type MaterialRow, type PriceListData, yearStart } from "./meta";
import { HiddenMark, retiredName } from "./HiddenMark";
import { CheckedListMark } from "./CheckedMark";
import { tagIdsOf } from "./smartTags";
import type { NameTagConfig } from "./names";
import { ListName } from "./ListName";
import { estimatedCost, isPlain, type NeedsCheck } from "./pricing";
import { type SetupGaps, setupGaps, importedDuplicates } from "./setup";
import { assessItem, type ItemAssessment, itemContext, type ItemProblem, type ItemProblemId, pricesDueDetail, tintsRow } from "./attention";
import { ANY_KIND, FilterBar, matchesSearch, NarrowedPill, PillSelect, TagFilter } from "./ListToolbar";
import { usePriceListWindows } from "./windows";

/**
 * Which items (FilterBar's tabs): active (in use, as the other lists' Active), split into visible (picked on jobs in ServiceM8) and
 * hidden (archived there, on standby: marked), then archived, all.
 */
type Tab = "active" | "visible" | "hidden" | "archived" | "all";
const TABS: { id: Tab; label: string }[] = [
    { id: "active", label: "Active" },
    { id: "visible", label: "Visible" },
    { id: "hidden", label: "Hidden" },
    { id: "archived", label: "Archived" },
    { id: "all", label: "All" },
];
/** The kinds of what needs attention (its chips), in the same words as Home's alerts, worst first: each an attention.ts problem. */
type Kind = "noNumber" | "dupNumber" | "low" | "servicem8Price" | "unknown" | "changed" | "check" | "costUpdate" | "itemsToUpdate" | "pricesDue" | "noCost" | "dupName" | "names" | "tagClash";
const KINDS: { id: Kind; label: string; problem: ItemProblemId }[] = [
    { id: "noNumber", label: "No item number", problem: "noNumber" },
    { id: "dupNumber", label: "Shared item number", problem: "sharedNumber" },
    { id: "low", label: "Making too little", problem: "low" },
    { id: "servicem8Price", label: "Priced by ServiceM8", problem: "servicem8Price" },
    { id: "unknown", label: "Unknown", problem: "unknown" },
    { id: "changed", label: "Changed in ServiceM8", problem: "changed" },
    { id: "check", label: "Price to update", problem: "check" },
    { id: "costUpdate", label: "Cost to update", problem: "costUpdate" },
    // Products only: items built from them aren't what they add up to now.
    { id: "itemsToUpdate", label: "Items to update", problem: "itemsToUpdate" },
    { id: "pricesDue", label: "Supplier price to check", problem: "pricesDue" },
    { id: "noCost", label: "No cost", problem: "noCost" },
    { id: "dupName", label: "Shared name", problem: "sharedName" },
    { id: "names", label: "Name to update", problem: "names" },
    { id: "tagClash", label: "Tag clash", problem: "tagClash" },
];
/** Narrowing by how it's set up or used (its Only pill): not things to put right, so not among what needs attention. */
type More = "checked" | "notChecked" | "linked" | "notLinked" | "overrides" | "noSupplier" | "noCostSet" | "typed" | "unused";
const MORE: { id: More; label: string }[] = [
    // Gone over and right (checked.ts), or not yet: working through the list.
    { id: "notChecked", label: "Not checked" },
    { id: "checked", label: "Checked" },
    // Items: priced from the Catalogue's products (packages.ts), or not (a typed price: to link, after the move to the Catalogue).
    { id: "linked", label: "Linked to Catalogue" },
    { id: "notLinked", label: "Not linked" },
    // Products: in a group, but priced their own way.
    { id: "overrides", label: "Overriding their group's price" },
    { id: "noSupplier", label: "No supplier price" },
    { id: "noCostSet", label: "No cost entered" },
    { id: "typed", label: "Price typed in" },
    // Only once jobs and job lines are read in full (data.lastUsed: before then, items not reached yet would look unused).
    { id: "unused", label: "Not used in 12 months" },
];
/** About costs and pricing: only with See costs (without it, the list has nothing to go on). */
const COST_ONLY: ReadonlySet<string> = new Set(["overrides", "check", "costUpdate", "itemsToUpdate", "pricesDue", "names", "low", "noCost", "noSupplier", "noCostSet", "typed"]);
/** Rows drawn at a time (the rest come with "Show more"), so big price lists stay quick. */
const PAGE = 200;

/**
 * The Items tab (ServiceM8's materials): search, filters, and a row per item. Clicking a row opens it. Hidden items (on standby)
 * and retired ones are listed when asked for (Show). `products`: the Catalogue's Products instead
 * (catalogue.ts), the same list of what items are built from, kept only here.
 */
export function MaterialsList(props: Readonly<{ data: PriceListData; openUuid?: string; onOpen: (uuid: string) => void; onNew: () => void; products?: boolean }>) {
    const { data } = props;
    const productsTab = props.products === true;
    const word = productsTab ? "product" : "item";
    // What uses each: an item, the bundles (not archived) holding it; a product, the items built from it.
    const usedIn = useMemo(() => {
        const out = new Map<string, number>();
        const add = (uuid: string) => out.set(uuid, (out.get(uuid) ?? 0) + 1);
        for (const b of data.bundles) if (b.active) for (const l of b.material_list) add(l.uuid);
        for (const parts of Object.values(data.packages)) for (const p of parts) add(p.uuid);
        return out;
    }, [data.bundles, data.packages]);
    const fmt = useMoney();
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    // Links (e.g. from Home) can open it on a filter: ?filter=check; or (a group's Items tab) on a group's items: ?inGroup=...
    // (not ?group=: that opens a group's window, which shouldn't filter the list).
    const search = new URLSearchParams(useSearch());
    const linked = search.get("filter");
    // Narrowed to a tag's items (?tag=... links here with one).
    const [tagId, setTagId] = useState(() => search.get("tag") ?? "");
    const tag = data.tags.find((t) => t.id === tagId);
    // The group it's narrowed to lives in the address (with any windows open): cleared there, keeping them.
    const windows = usePriceListWindows();
    // The business's days (its time zone), as its Stats count them.
    const zone = useCompany().timeZone;
    const inGroup = data.groups.find((g) => g.id === windows.params.get("inGroup"));
    // Just some items (?items=a,b: e.g. those Tags settings says clash), until cleared.
    const only = useMemo(() => {
        const listed = windows.params.get("items");
        return listed ? new Set(listed.split(",")) : null;
    }, [windows.params.get("items")]);
    // Costs, markups and pricing (and the filters about them) only with See costs: the server leaves them out otherwise.
    const seesCosts = can(data, "costs.view");
    const kindsShown = KINDS.filter((k) => seesCosts || !COST_ONLY.has(k.id));
    const moreShown = MORE.filter((o) => (seesCosts || !COST_ONLY.has(o.id)) && (o.id !== "unused" || data.lastUsed) && MORE_FOR[productsTab ? "products" : "items"].has(o.id));
    // Links (e.g. Home's alerts) open it on a tab, narrowed to what needs attention (one kind, or "attention": any), or an Only.
    // A product isn't sold itself, so it's never visible or hidden on jobs: in use, or archived.
    const tabs = productsTab ? TABS.filter((t) => t.id !== "visible" && t.id !== "hidden") : TABS;
    const [tab, setTab] = useState<Tab>(() => (tabs.some((t) => t.id === linked) ? (linked as Tab) : "active"));
    const [kind, setKind] = useState<Kind | typeof ANY_KIND | null>(() => (linked === "attention" ? ANY_KIND : (kindsShown.find((k) => k.id === linked)?.id ?? null)));
    const [more, setMore] = useState<More | null>(() => moreShown.find((o) => o.id === linked)?.id ?? null);
    // Products: with how many items are built from each.
    const parts = productsTab;
    const [shown, setShown] = useState(PAGE);

    const withSupplierPrices = useMemo(() => new Set(data.supplierPrices.map((p) => p.materialUuid)), [data.supplierPrices]);
    /** Every item, with what the list and its filters need: what needs attention is attention.ts's (as its window's and Home's). */
    const all = useMemo(() => {
        const ctx = itemContext(data);
        return data.materials.filter((m) => isProduct(m) === productsTab).map((m) => {
            const { rate, tax } = itemTax(data, m);
            const pricing = data.itemPricing[m.uuid];
            const gaps = setupGaps(m, { hasSupplierPrice: withSupplierPrices.has(m.uuid), hasPricing: pricing !== undefined });
            return { m, rate, tax, pricing, gaps, ...assessItem(m, ctx) };
        });
    }, [data, withSupplierPrices, productsTab]);
    /** How many items each filter shows (search aside), for the counts by each. */
    // Narrowed to a group, or a tag: its items only (counts too). One no longer there: everything.
    const scoped = useMemo(
        () => all.filter((row) => (!inGroup || row.pricing?.groupId === inGroup.id) && (!tag || tagIdsOf(data, row.m.uuid).includes(tag.id)) && (!only || only.has(row.m.uuid))),
        [all, inGroup, tag, data.itemTags, only],
    );
    // On its tab (and Only), what needs attention among those: each kind's count (red when any of them is).
    const inTabNow = useMemo(() => scoped.filter((row) => inTab(tab, row, data) && (more === null || inMore(more, row, data, zone))), [scoped, tab, more, data, zone]);
    const kinds = kindsShown.map((k) => {
        const withIt = inTabNow.filter((row) => hasKind(k.id, row));
        return { id: k.id, label: k.label, count: withIt.length, danger: withIt.some((row) => row.problems.some((p) => p.id === k.problem && p.danger)) };
    });
    const groupNames = useMemo(() => new Map(data.groups.map((g) => [g.id, g.name])), [data.groups]);
    // Items ServiceM8 prices itself (its pricing strategy): their pricing here isn't used.
    const inServiceM8 = useMemo(() => new Set(data.pricedInServiceM8), [data.pricedInServiceM8]);
    // Searched by name, item number, barcode, its group's name and its tags' ("deadbolt" finds the Deadbolts group's items, and those tagged Deadbolt).
    const rows = useMemo(
        () =>
            inTabNow.filter(
                (row) => (kind === null || hasKind(kind, row)) && matchesSearch(itemSearchText(row.m, groupNames.get(row.pricing?.groupId ?? ""), tagNamesOf(data, row.m.uuid)), query),
            ),
        [inTabNow, data, query, kind, groupNames],
    );
    // A change of what's shown starts from the top.
    const shownFrom = <T,>(set: (v: T) => void) => (v: T) => {
        set(v);
        setShown(PAGE);
    };

    return (
        <div className="page is-wide">
            <PageHeader
                title={productsTab ? "Products" : "Items"}
                icon={productsTab ? <Puzzle className="size-6" /> : <Package className="size-6" />}
                tone={productsTab ? "rose" : "blue"}
                subtitle={
                    productsTab
                        ? `What your items are linked to, kept only here: ${plural(all.filter((r) => inUse(r.m)).length, "product")}, with their costs, supplier prices and pricing.`
                        : `Your ServiceM8 materials: ${plural(all.filter((r) => r.m.active).length, "item")} for sale, and ${plural(all.filter((r) => r.m.hidden).length, "item")} hidden, kept ready for when they're needed.`
                }
                actions={
                    can(data, "items.add") ? (
                        <button type="button" className="btn-primary" onClick={props.onNew}>
                            <Plus className="size-4" /> New {word}
                        </button>
                    ) : null
                }
            />
            {whyNot(data, "items.edit") ? (
                <div className="mb-4">
                    <Notice>{whyNot(data, "items.edit")}</Notice>
                </div>
            ) : null}
            {can(data, "items.archive") && !productsTab ? <MergeImported data={data} /> : null}
            {kind === "unknown" && can(data, "items.review") && rows.length > 0 && !productsTab ? <MarkAllKnown uuids={rows.map((r) => r.m.uuid)} /> : null}
            {(kind === "costUpdate" && can(data, "costs.edit") && rows.length > 0) || data.costsUpdating ? (
                <UpdateAllCosts uuids={kind === "costUpdate" ? rows.map((r) => r.m.uuid) : []} running={data.costsUpdating} />
            ) : null}
            <Panel flush>
                <FilterBar
                    searchLabel={`Search ${word}s`}
                    placeholder={seesCosts ? "Search by name, item number, barcode, group or tag" : "Search by name, item number, barcode or tag"}
                    query={query}
                    onQuery={shownFrom(setQuery)}
                    tabs={tabs.map((t) => ({ ...t, count: scoped.filter((row) => inTab(t.id, row, data)).length }))}
                    tab={tab}
                    onTab={shownFrom(setTab)}
                    kinds={kinds}
                    anyCount={inTabNow.filter((row) => hasKind(ANY_KIND, row)).length}
                    kind={kind}
                    onKind={shownFrom(setKind)}
                >
                    {/* Narrowed from elsewhere (a group's Items tab, Tags settings): said, with its × to show every item again. */}
                    {inGroup ? (
                        <NarrowedPill onClear={() => windows.set({ inGroup: null })}>
                            <Layers className="size-3.5 text-brand-ink" /> {inGroup.name}
                        </NarrowedPill>
                    ) : null}
                    {only ? <NarrowedPill onClear={() => windows.set({ items: null })}>Just {plural(only.size, "item")}</NarrowedPill> : null}
                    <PillSelect label="Only" value={more ?? ""} anyLabel="Everything" options={moreShown.map((o) => ({ value: o.id, label: o.label }))} onChange={(v) => shownFrom(setMore)((v || null) as More | null)} />
                    <TagFilter tags={data.tags} value={tag ? tag.id : ""} onChange={shownFrom(setTagId)} />
                </FilterBar>
                <table className={clsx("list is-fixed", seesCosts ? "min-w-[1000px]" : "min-w-[600px]")}>
                    {/* The item number and figures get just the room they need; the name gets the rest. */}
                    <colgroup>
                        <col className="w-28" />
                        <col />
                        <col className="w-32" />
                        {seesCosts ? (
                            <>
                                <col className="w-24" />
                                <col className="w-20" />
                                <col className="w-24" />
                                <col className="w-52" />
                            </>
                        ) : null}
                        {parts ? <col className="w-24" /> : null}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Item #</th>
                            <th>Name</th>
                            <th className="text-right">Price</th>
                            {seesCosts ? (
                                <>
                                    <th className="text-right">Cost</th>
                                    <th className="text-right">Markup</th>
                                    <th className="text-right" title="Estimated profit per unit before tax: its price less its cost">
                                        Est. profit
                                    </th>
                                    <th>Pricing</th>
                                </>
                            ) : null}
                            {parts ? (
                                <th className="text-right" title={productsTab ? "How many items are linked to it" : "How many bundles it's in"}>
                                    Used in
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.slice(0, shown).map((row) => {
                            const { m, rate, pricing, check, profit, numberIssue, problems } = row;
                            return (
                                <MaterialLine
                                    key={m.uuid}
                                    m={m}
                                    checked={data.checked[m.uuid]}
                                    problems={problems}
                                    numberIssue={numberIssue}
                                    tags={data.nameTags}
                                    basis={basisLabel(m.price_includes_taxes, rate)}
                                    basisFull={basisLabel(m.price_includes_taxes, rate, true)}
                                    cost={shownCost(data, m)}
                                    costTo={m.uuid in data.costUpdates ? shownCost(data, { ...m, cost: estimatedCost(data, m) }).amount : null}
                                    costCheck={pricesDueOf(itemContext(data), m.uuid)}
                                    setBy={inServiceM8.has(m.uuid) ? "ServiceM8 strategy" : describePricingShort(pricing, fmt)}
                                    typed={!pricing || isPlain(pricing)}
                                    group={pricing?.groupId ? { id: pricing.groupId, name: groupNames.get(pricing.groupId) ?? "A group", overrides: pricing.overridesGroup === true } : undefined}
                                    check={check}
                                    markupPct={profit.markupPct}
                                    total={profit.profitNet}
                                    minMarkup={data.settings.minMarkup}
                                    seesCosts={seesCosts}
                                    usedIn={parts ? (usedIn.get(m.uuid) ?? 0) : undefined}
                                    usedWord={productsTab ? "item" : "bundle"}
                                    open={props.openUuid === m.uuid}
                                    onOpen={() => props.onOpen(m.uuid)}
                                />
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{all.length ? `No ${word}s match.` : `No ${word}s yet.`}</p> : null}
                {rows.length > shown ? (
                    <div className="border-t border-line p-3 text-center">
                        <button type="button" className="link-button text-sm" onClick={() => setShown((n) => n + PAGE)}>
                            Show more ({(rows.length - shown).toLocaleString()} left)
                        </button>
                    </div>
                ) : null}
            </Panel>
        </div>
    );
}

function MaterialLine(
    props: Readonly<{
        m: MaterialRow;
        checked: CheckedMark | undefined;
        problems: ItemProblem[];
        numberIssue: ItemAssessment["numberIssue"];
        tags: NameTagConfig;
        basis: string;
        /** The same, with the rate named as ServiceM8 has it (on hover). */
        basisFull: string;
        /** ServiceM8's cost (as the Price column, ServiceM8's price). */
        cost: { amount: number; label: string; fullLabel: string };
        /** While it has a cost to update: the cost it'll have (pricing.ts estimatedCost, what its markup and profit are on), else null. */
        costTo: number | null;
        /** Its supplier prices to check (its cost follows them): said on hover, its cost marked. */
        costCheck: string | null;
        setBy: string;
        typed: boolean;
        /** The group it uses, if any. */
        group?: { id: string; name: string; overrides: boolean };
        check: NeedsCheck | null;
        markupPct: number | null;
        /** What it makes on its cost (attention.ts profit). */
        total: number | null;
        minMarkup: number;
        /** Show its cost, markup and pricing (See costs). */
        seesCosts: boolean;
        /** Hidden, or a product: what uses it (its own column), how many, and what they are. */
        usedIn?: number;
        usedWord?: string;
        open: boolean;
        onOpen: () => void;
    }>,
) {
    const { m, problems } = props;
    const fmt = useMoney();
    const low = problems.some((p) => p.id === "low");
    return (
        // The name button covers the whole row (after:inset-0): click selects it, double-click opens the item.
        <tr className={clsx("relative", rowTone(problems), props.open ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")} aria-current={props.open || undefined}>
            {/* Every row is the same height (the name and a small line under it fit). */}
            <td className="h-14 py-2">
                <span className="flex min-w-0 items-center gap-1">
                    <span className="min-w-0">
                        <ItemNumberCell itemNumber={m.item_number} issue={props.numberIssue} />
                    </span>
                </span>
            </td>
            <td className="py-2">
                <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(props.onOpen)}>
                    <NameCell m={m} checked={props.checked} tags={props.tags} problems={problems.map((p) => p.text)} danger={problems.some((p) => p.danger)} />
                </button>
            </td>
            {/* The amount on its own line (never cut short), and under it, small: how it's taxed. */}
            <td className="text-right">
                <div className="flex justify-end whitespace-nowrap">
                    {/* Its pricing gives a different price now: flagged, hover for the price it should be. */}
                    {props.check ? (
                        <ListWarning title={checkText(props.check, fmt.money(props.check.target))}>
                            <span className="tabular-nums">{fmt.money(m.price)}</span>
                        </ListWarning>
                    ) : (
                        <span className="tabular-nums">{fmt.money(m.price)}</span>
                    )}
                </div>
                <div className="flex min-w-0 justify-end">
                    <TaxLabel label={props.basis} full={props.basisFull} />
                </div>
            </td>
            {props.seesCosts ? <CostCells {...props} low={low} /> : null}
            {props.usedIn === undefined ? null : (
                <td className={clsx("text-right text-sm tabular-nums", props.usedIn ? undefined : "text-muted")}>{props.usedIn ? plural(props.usedIn, props.usedWord ?? "bundle") : "Not used"}</td>
            )}
        </tr>
    );
}

/**
 * The ServiceM8 name on one line (the name and its tags in brackets, cut short if need be: the
 * whole name shows on hover), and an icon when its name reads differently from what its tags give it. Under it, small: the lines ServiceM8 shows under the name (e.g. special order)
 * and the stock, when there are any.
 */
function NameCell({ m, checked, tags, problems, danger }: Readonly<{ m: MaterialRow; checked: CheckedMark | undefined; tags: NameTagConfig; problems: string[]; danger: boolean }>) {
    const fmt = useMoney();
    return (
        <ListName
            name={m.name}
            tags={tags}
            problems={problems}
            danger={danger}
            mark={
                <>
                    <CheckedListMark mark={checked} />
                    <HiddenMark item={m} />
                </>
            }
            struck={retiredName(m) !== undefined}
            under={m.item_is_inventoried ? `${fmt.number(m.quantity_in_stock)} in stock` : undefined}
        />
    );
}

/**
 * How its price is set: a typed price says Manual (grey); otherwise what sets it on top (its
 * group, which opens it, or its own pricing) with the rule in small text underneath. In a
 * group but overriding its price: the group, marked Overrides, and its own rule underneath.
 */
function PricingCell({ group, setBy, typed }: Readonly<{ group?: { id: string; name: string; overrides: boolean }; setBy: string; typed: boolean }>) {
    const windows = usePriceListWindows();
    if (!group && typed) return <div className="truncate text-muted" title="No pricing rule: the price is typed in">Typed price</div>;
    return (
        <>
            {group ? (
                // "relative" lifts it above the row's click area: a click opens the group, not the item.
                <button
                    type="button"
                    className="link-button relative flex max-w-full min-w-0 items-center gap-1 font-normal"
                    title={group.overrides ? `In the ${group.name} group, but overriding its price (priced its own way): open the group` : `Priced by the ${group.name} group: open it`}
                    onClick={() => windows.openGroup(group.id)}
                >
                    <Layers className="size-3.5 shrink-0" />
                    <span className="truncate">{group.name}</span>
                    {group.overrides ? <Badge tone="blue">Overrides</Badge> : null}
                </button>
            ) : (
                <div className="truncate">Own pricing</div>
            )}
            <div className="truncate text-xs text-muted" title={setBy}>
                {setBy}
            </div>
        </>
    );
}

/** A row that needs attention is tinted: red for a problem that's red (attention.ts), amber for anything else to sort out. */
function rowTone(problems: readonly ItemProblem[]) {
    if (problems.some((p) => p.danger)) return "is-danger";
    // Supplier prices to check aren't tinted (attention.ts tintsRow): marked by its cost.
    return problems.some(tintsRow) ? "is-warning" : null;
}

/** Its cost, markup and pricing (See costs). */
function CostCells(
    props: Readonly<{
        cost: { amount: number; label: string; fullLabel: string };
        costTo: number | null;
        costCheck: string | null;
        markupPct: number | null;
        low: boolean;
        total: number | null;
        minMarkup: number;
        group?: { id: string; name: string; overrides: boolean };
        setBy: string;
        typed: boolean;
    }>,
) {
    const fmt = useMoney();
    return (
        <>
            {/* As the price: the amount, and under it how it's taxed. */}
            <td className="text-right">
                <div className="flex justify-end whitespace-nowrap">
                    {/* A cost to update: ServiceM8's, flagged; hover for the cost it'll have (as a price to update is). */}
                    {/* Its supplier prices to check: marked too, both said when there's both. */}
                    {props.costTo === null && props.costCheck === null ? (
                        <span className="tabular-nums">{fmt.money(props.cost.amount)}</span>
                    ) : (
                        <ListWarning
                            title={[props.costTo === null ? null : `Cost to update: it'll be ${fmt.money(props.costTo)}. Its markup and profit are on that.`, props.costCheck, "Open the item to see."].filter(Boolean).join(" ")}
                        >
                            <span className="tabular-nums">{fmt.money(props.cost.amount)}</span>
                        </ListWarning>
                    )}
                </div>
                <div className="flex min-w-0 justify-end">
                    <TaxLabel label={props.cost.label} full={props.cost.fullLabel} />
                </div>
            </td>
            <td className="text-right tabular-nums">
                <MarkupCell markupPct={props.markupPct} low={props.low} minMarkup={props.minMarkup} />
            </td>
            <td className="text-right tabular-nums">
                <TotalCell total={props.total} />
            </td>
            <td>
                <PricingCell group={props.group} setBy={props.setBy} typed={props.typed} />
            </td>
        </>
    );
}

/** A tax label under an amount ("inc GST"): cut with "…" only when a long one doesn't fit the column, in full (as ServiceM8 names the rate) on hover. */
function TaxLabel({ label, full }: Readonly<{ label: string; full: string }>) {
    return (
        <span className="min-w-0 truncate text-xs text-muted" title={full}>
            {label}
        </span>
    );
}

/** Its estimated profit per unit before tax (its price less its cost); "-" when that can't be told (no cost). */
export function TotalCell({ total }: Readonly<{ total: number | null }>) {
    const fmt = useMoney();
    return total === null ? <span className="text-muted">-</span> : <>{fmt.money(total)}</>;
}

/** Its markup on its cost, flagged red when it's under the minimum (Settings). */
export function MarkupCell({ markupPct, low, minMarkup }: Readonly<{ markupPct: number | null; low: boolean; minMarkup: number }>) {
    const fmt = useMoney();
    if (markupPct === null) return <span className="text-muted">-</span>;
    const shown = fmt.percent(Math.round(markupPct));
    if (!low) return <>{shown}</>;
    const why = minMarkup > 0 ? `under your ${minMarkup}% minimum` : "nothing, or a loss";
    return (
        <ListWarning tone="danger" title={`Makes too little on its cost: ${why} (Price List settings). Open the item to change its pricing.`}>
            {shown}
        </ListWarning>
    );
}

/**
 * The item number, or what needs doing about it in its place: none ("Missing"), or shared with
 * another item (the number, flagged). Its row is tinted too (is-warning): every item needs its own.
 */
export function ItemNumberCell({ itemNumber, issue }: Readonly<{ itemNumber: string; issue: ItemAssessment["numberIssue"] }>) {
    if (!issue) {
        return (
            <span className="block truncate text-muted" title={itemNumber || undefined}>
                {itemNumber || "-"}
            </span>
        );
    }
    return issue === "missing" ? (
        <ListWarning title="Every item needs an item number. Open it to set one.">Missing</ListWarning>
    ) : (
        <ListWarning title={`Every item needs its own item number: another item also uses "${itemNumber}". Open it to change one.`}>{itemNumber}</ListWarning>
    );
}

/** Its supplier prices to check, in full (its cost follows them), or null. */
function pricesDueOf(ctx: ReturnType<typeof itemContext>, uuid: string) {
    const due = ctx.pricesDue.get(uuid);
    return due ? pricesDueDetail(due) : null;
}

/** Hover text for the needs-checking icon by the price. */
function checkText(check: NeedsCheck, target: string) {
    if (check.reason === "edited") return `Price needs checking: it was edited in ServiceM8, but its pricing gives ${target}. Open the item to reset or keep it.`;
    return `Price needs checking: its pricing now gives ${target}. Open the item to update the price.`;
}

/** The "Needs attention" filters: each shows the items with that problem (attention.ts). */
type ListRow = { m: MaterialRow; gaps: SetupGaps; problems: readonly ItemProblem[] };

/** Whether an item is on this tab. Archived: not those merged into another as duplicates (merge.ts): the one kept is the item. */
function inTab(tab: Tab, { m }: ListRow, data: PriceListData): boolean {
    if (tab === "active") return inUse(m);
    if (tab === "visible") return m.active;
    if (tab === "hidden") return m.hidden;
    if (tab === "archived") return !inUse(m) && !data.mergedItems[m.uuid];
    return true;
}

/** Whether it has this kind of what needs attention (any: anything at all). */
const hasKind = (kind: Kind | typeof ANY_KIND, row: ListRow) =>
    kind === ANY_KIND ? row.problems.length > 0 : row.problems.some((p) => p.id === KINDS.find((k) => k.id === kind)?.problem);

/** Which Only choices each list has: linked are items'; supplier prices, costs and pricing products'. */
const MORE_FOR: Record<"items" | "products", ReadonlySet<More>> = {
    items: new Set(["notChecked", "checked", "linked", "notLinked", "unused"]),
    products: new Set(["notChecked", "checked", "overrides", "noSupplier", "noCostSet", "typed", "unused"]),
};

/** Whether it's one of these (its Only pill). */
function inMore(more: More, row: ListRow, data: PriceListData, zone: string | null): boolean {
    const { m } = row;
    if (more === "checked" || more === "notChecked") return inUse(m) && (data.checked[m.uuid] !== undefined) === (more === "checked");
    if (more === "linked" || more === "notLinked") return inUse(m) && (data.packages[m.uuid] !== undefined) === (more === "linked");
    if (more === "overrides") return data.itemPricing[m.uuid]?.overridesGroup === true;
    if (more === "unused") return inUse(m) && data.lastUsed !== null && (data.lastUsed[m.uuid] ?? "") < yearStart(zone);
    return inUse(m) && row.gaps[more === "noCostSet" ? "noCost" : more];
}

/**
 * On the Cost to update filter: update every cost it shows (search included) to what its supplier price or what's in it give,
 * after asking (useConfirm). In the background (there can be hundreds, each a ServiceM8 save): items
 * leave the list as each is saved. While it runs, it says so wherever the list is open.
 */
function UpdateAllCosts({ uuids, running }: Readonly<{ uuids: string[]; running: boolean }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const update = async () => {
        const yes = await confirm({
            title: `Update ${plural(uuids.length, "cost")}?`,
            message: `${uuids.length === 1 ? "Its cost goes" : "Their costs go"} to what ${uuids.length === 1 ? "its" : "their"} supplier price or what's in it give. Each is changed in ServiceM8, and leaves this list as it's saved.`,
            confirmLabel: `Update ${plural(uuids.length, "cost")}`,
        });
        if (!yes) return;
        setError(null);
        saver.updateCosts(uuids).catch((err: Error) => setError(err));
    };
    if (running) {
        return (
            <div className="mb-4">
                <Notice>Updating costs. Items leave this list as each is saved in ServiceM8.</Notice>
            </div>
        );
    }
    return (
        <div className="mb-4 space-y-2">
            <Notice
                actions={
                    <button type="button" className="btn-secondary" onClick={() => void update()}>
                        Update all
                    </button>
                }
            >
                <strong>Costs to update.</strong> Their supplier prices or what's in them give other costs now. Costs never change by themselves: update each from its window, or all of them here.
            </Notice>
            <ErrorNotice error={error} />
        </div>
    );
}

/**
 * Items an import in ServiceM8 brought in again (its supplier import doesn't see archived, so hidden, items: setup.ts
 * importedDuplicates): each merged into the item it copies in one go, its cost (the supplier's new one) taken, after asking. Above
 * the list on any filter, while there are some.
 */
function MergeImported({ data }: Readonly<{ data: PriceListData }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const pairs = importedDuplicates(data.materials.filter((m) => !isProduct(m)), new Set(data.unknownItems));
    if (!pairs.length) return null;
    const n = pairs.length;
    const merge = async () => {
        const yes = await confirm({
            title: `Merge ${plural(n, "duplicate")}?`,
            message: `Each existing item takes its copy's cost, bundles and supplier prices. The ${n === 1 ? "copy is" : "copies are"} archived.`,
            confirmLabel: `Merge ${plural(n, "duplicate")}`,
        });
        if (!yes) return;
        setBusy(true);
        setError(null);
        saver
            .mergeImported()
            .then((r) => {
                const them = r.failed === 1 ? "it" : "them";
                if (r.failed) setError(new Error(`${plural(r.failed, "duplicate")} couldn't be merged: open ${them} to see why.`));
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <div className="mb-4 space-y-2">
            <Notice
                tone="warning"
                actions={
                    <button type="button" className="btn-secondary" disabled={busy} onClick={() => void merge()}>
                        {busy ? "Merging..." : "Merge all"}
                    </button>
                }
            >
                <strong>{plural(n, "duplicate")} from ServiceM8.</strong> New {n === 1 ? "item shares its" : "items share their"} item number with {n === 1 ? "an existing one" : "existing ones"},
                likely from an import (it doesn&apos;t see hidden or archived items).
                <span className="mt-0.5 block text-xs opacity-80" title={pairs.map((p) => p.keep.name).join(", ")}>
                    {pairs
                        .slice(0, 3)
                        .map((p) => p.keep.name)
                        .join(", ")}
                    {n > 3 ? ` and ${n - 3} more` : ""}
                </span>
            </Notice>
            <ErrorNotice error={error} />
        </div>
    );
}

/**
 * On the Unknown items filter: keep every item it shows (search included) in one go, after asking
 * (useConfirm: for day one, when every item is unknown, or after checking a batch). Mistakes are
 * archived one by one.
 */
function MarkAllKnown({ uuids }: Readonly<{ uuids: string[] }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const mark = async () => {
        const yes = await confirm({
            title: `Keep ${plural(uuids.length, "item")}?`,
            message: "Every item shown is marked as a real item: they stop showing as unknown.",
            confirmLabel: `Keep ${plural(uuids.length, "item")}`,
        });
        if (!yes) return;
        setError(null);
        saver.markKnown(uuids).catch((err: Error) => setError(err));
    };
    return (
        <div className="mb-4 space-y-2">
            <Notice
                actions={
                    <button type="button" className="btn-secondary" onClick={() => void mark()}>
                        Keep all
                    </button>
                }
            >
                <strong>Unknown items from ServiceM8.</strong> Please review them, then keep or archive each (archive from its window).
                <span className="mt-0.5 block text-xs opacity-80">Unknown items can come from part numbers on jobs that don&apos;t match an item, or from your accounting software once invoices are approved.</span>
            </Notice>
            <ErrorNotice error={error} />
        </div>
    );
}
